import React, { useState, useEffect } from 'react';
import tw from 'twin.macro';
import styled from 'styled-components';
import { useMutation, useQuery } from '@apollo/client';
import {
  UPDATE_CAR_TRIP_PREFERENCES,
  UPDATE_CAR,
  GET_CAR,
} from '../../services/carService/queries';
import { toast } from 'react-toastify';
import { ITripPreferences } from '../../../__generated__/graphql';
import { Button } from '../button';
import { IGetCarQuery } from '../../services/carService/__generated__/queries.generated';

const Container = styled.div`
  ${tw`p-6 max-w-md`}
`;

const Header = styled.div`
  ${tw`flex items-center gap-2 mb-6`}
`;

const Title = styled.h1`
  ${tw`text-2xl font-bold`}
`;

const InfoIcon = styled.span`
  ${tw`text-text-tertiary`}
`;

const Section = styled.div`
  ${tw`mb-6`}
`;

const SectionTitle = styled.h2`
  ${tw`text-base font-semibold mb-2`}
`;

const SelectWrapper = styled.div`
  ${tw`relative`}
`;

const Select = styled.select`
  ${tw`w-full p-2 border border-border-default rounded-md appearance-none bg-white h-[44px]`}
`;

const Input = styled.input`
  ${tw`w-full p-2 border border-border-default rounded-md bg-white h-[44px]`}
`;

const ErrorText = styled.div`
  ${tw`text-red-500 text-sm mt-1`}
`;

const ToggleContainer = styled.div`
  ${tw`flex items-center justify-between mb-3`}
`;

const ToggleLabel = styled.label`
  ${tw`text-base font-semibold`}
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background-color: #2563eb;
  }

  &:checked + span:before {
    transform: translateX(24px);
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  border-radius: 24px;
  transition: 0.3s;

  &:before {
    position: absolute;
    content: '';
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    border-radius: 50%;
    transition: 0.3s;
  }
`;

const InputWrapper = styled.div`
  ${tw`mt-2`}
`;

const LocationText = styled.div`
  ${tw`w-full p-3 text-text-secondary`}
`;

const SaveButtonContainer = styled.div`
  ${tw`mt-4`}
`;

const SubmitButton = styled.button`
  ${tw`w-full py-2 px-4 bg-black text-white font-semibold rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2`}

  &:hover {
    ${tw`bg-blue-700`}
  }
`;

interface TripPreferencesProps {
  carId: string;
  tripPreferences: ITripPreferences | null;
}

const TripPreferences: React.FC<TripPreferencesProps> = ({ carId, tripPreferences }) => {
  const [advanceNoticeHours, setAdvanceNoticeHours] = useState(
    tripPreferences?.advanceNoticeHours || 1,
  );
  const [tripBufferHours, setTripBufferHours] = useState(tripPreferences?.tripBufferHours || 3);

  const {
    data,
    loading: carLoading,
    error: carError,
    refetch: refetchCar,
  } = useQuery<IGetCarQuery>(GET_CAR, {
    variables: { id: carId },
    skip: !carId,
    fetchPolicy: 'cache-and-network', // Update to see changes immediately after mutations
  });

  // Initialize minimum and maximum rental days from car data
  const [minimumRentalDays, setMinimumRentalDays] = useState<number | null>(
    data?.car?.minimumRentalDays ?? null,
  );
  const [maximumRentalDays, setMaximumRentalDays] = useState<number | null>(
    data?.car?.maximumRentalDays ?? null,
  );
  const [rentalDaysError, setRentalDaysError] = useState<string | null>(null);

  // Toggle states - true means the field is enabled and must have a value
  const [minimumRentalDaysEnabled, setMinimumRentalDaysEnabled] = useState<boolean>(
    data?.car?.minimumRentalDays !== null && data?.car?.minimumRentalDays !== undefined,
  );
  const [maximumRentalDaysEnabled, setMaximumRentalDaysEnabled] = useState<boolean>(
    data?.car?.maximumRentalDays !== null && data?.car?.maximumRentalDays !== undefined,
  );

  // Update state when car data loads or tripPreferences prop changes
  useEffect(() => {
    if (data?.car) {
      const minDays = data.car.minimumRentalDays ?? null;
      const maxDays = data.car.maximumRentalDays ?? null;

      setMinimumRentalDays(minDays);
      setMaximumRentalDays(maxDays);
      setMinimumRentalDaysEnabled(minDays !== null && minDays !== undefined);
      setMaximumRentalDaysEnabled(maxDays !== null && maxDays !== undefined);
    }
  }, [data?.car]);

  // Update trip preferences state when prop changes (after mutation completes)
  useEffect(() => {
    if (tripPreferences) {
      setAdvanceNoticeHours(tripPreferences.advanceNoticeHours || 1);
      setTripBufferHours(tripPreferences.tripBufferHours || 3);
    }
  }, [tripPreferences]);

  // Validate rental days whenever they change
  useEffect(() => {
    if (minimumRentalDays !== null && maximumRentalDays !== null) {
      if (maximumRentalDays <= minimumRentalDays) {
        setRentalDaysError('Maximum rental days must be greater than minimum rental days');
      } else {
        setRentalDaysError(null);
      }
    } else {
      setRentalDaysError(null);
    }
  }, [minimumRentalDays, maximumRentalDays]);

  const [updateTripPreferences, { loading: tripPreferencesLoading }] = useMutation(
    UPDATE_CAR_TRIP_PREFERENCES,
    {
      refetchQueries: [{ query: GET_CAR, variables: { id: carId } }],
      awaitRefetchQueries: true, // Wait for refetch to complete before showing success
      onCompleted: data => {
        // Update local state immediately after successful mutation
        if (data?.updateCarTripPreferences?.tripPreferences) {
          const prefs = data.updateCarTripPreferences.tripPreferences;
          setAdvanceNoticeHours(prefs.advanceNoticeHours || 1);
          setTripBufferHours(prefs.tripBufferHours || 3);
        }
      },
    },
  );

  const [updateCar, { loading: carUpdateLoading }] = useMutation(UPDATE_CAR, {
    refetchQueries: [{ query: GET_CAR, variables: { id: carId } }],
    awaitRefetchQueries: true, // Wait for refetch to complete before showing success
    onCompleted: data => {
      // Update local state immediately after successful mutation
      if (data?.updateCar) {
        const minDays = data.updateCar.minimumRentalDays ?? null;
        const maxDays = data.updateCar.maximumRentalDays ?? null;

        setMinimumRentalDays(minDays);
        setMaximumRentalDays(maxDays);
        setMinimumRentalDaysEnabled(minDays !== null && minDays !== undefined);
        setMaximumRentalDaysEnabled(maxDays !== null && maxDays !== undefined);
      }
    },
  });

  const validateRentalDays = (): boolean => {
    setRentalDaysError(null);

    // If toggle is enabled, value must be set
    if (
      minimumRentalDaysEnabled &&
      (minimumRentalDays === null || minimumRentalDays === undefined)
    ) {
      setRentalDaysError('Please enter a value for minimum rental days');
      return false;
    }

    if (
      maximumRentalDaysEnabled &&
      (maximumRentalDays === null || maximumRentalDays === undefined)
    ) {
      setRentalDaysError('Please enter a value for maximum rental days');
      return false;
    }

    // If both are set, max must be greater than min
    if (minimumRentalDays !== null && maximumRentalDays !== null) {
      if (maximumRentalDays <= minimumRentalDays) {
        setRentalDaysError('Maximum rental days must be greater than minimum rental days');
        return false;
      }
    }

    // Validate individual values
    if (minimumRentalDays !== null && minimumRentalDays < 1) {
      setRentalDaysError('Minimum rental days must be at least 1');
      return false;
    }

    if (maximumRentalDays !== null && maximumRentalDays < 1) {
      setRentalDaysError('Maximum rental days must be at least 1');
      return false;
    }

    return true;
  };

  const onSubmit = async () => {
    // Validate rental days
    if (!validateRentalDays()) {
      return;
    }

    try {
      // Update trip preferences - awaitRefetchQueries will ensure queries are refetched
      await updateTripPreferences({
        variables: {
          carId,
          tripPreferencesData: {
            advanceNoticeHours: advanceNoticeHours,
            tripBufferHours: tripBufferHours,
          },
        },
      });

      // Update car with minimum and maximum rental days
      // Only send values if toggles are enabled
      // awaitRefetchQueries will ensure queries are refetched after this completes
      await updateCar({
        variables: {
          id: carId,
          updateCarInput: {
            minimumRentalDays: minimumRentalDaysEnabled ? minimumRentalDays : null,
            maximumRentalDays: maximumRentalDaysEnabled ? maximumRentalDays : null,
          },
        },
      });

      // Manually refetch to ensure UI updates immediately for parent components
      await refetchCar();

      toast.success('Trip preferences updated successfully');
    } catch (error) {
      toast.error('Error updating trip preferences');
    }
  };

  const loading = tripPreferencesLoading || carUpdateLoading;

  return (
    <Container>
      <Header>
        <Title>Trip preferences</Title>
        <InfoIcon>ⓘ</InfoIcon>
      </Header>

      <Section>
        <SectionTitle>Current location</SectionTitle>
        <LocationText>
          {data?.car?.location?.address}, {data?.car?.location?.city},
          {' ' + data?.car?.location?.state} {' ' + data?.car?.location?.zipCode}
        </LocationText>
      </Section>

      <Section>
        <SectionTitle>Advance notice</SectionTitle>
        <SelectWrapper>
          <Select
            value={advanceNoticeHours}
            onChange={e => setAdvanceNoticeHours(parseInt(e.target.value))}
          >
            <option value="1">1 hour</option>
            <option value="2">2 hours</option>
            <option value="3">3 hours</option>
            <option value="6">6 hours</option>
            <option value="12">12 hours</option>
            <option value="24">24 hours</option>
          </Select>
        </SelectWrapper>
      </Section>

      <Section>
        <SectionTitle>Trip buffer</SectionTitle>
        <SelectWrapper>
          <Select
            value={tripBufferHours}
            onChange={e => setTripBufferHours(parseInt(e.target.value))}
          >
            <option value="1">1 hour</option>
            <option value="2">2 hours</option>
            <option value="3">3 hours</option>
            <option value="6">6 hours</option>
            <option value="12">12 hours</option>
          </Select>
        </SelectWrapper>
      </Section>

      <Section>
        <ToggleContainer>
          <ToggleLabel>Minimum rental days</ToggleLabel>
          <ToggleSwitch>
            <ToggleInput
              type="checkbox"
              checked={minimumRentalDaysEnabled}
              onChange={e => {
                const enabled = e.target.checked;
                setMinimumRentalDaysEnabled(enabled);
                if (!enabled) {
                  setMinimumRentalDays(null);
                } else if (minimumRentalDays === null) {
                  // Set a default value when enabling
                  setMinimumRentalDays(1);
                }
              }}
            />
            <ToggleSlider />
          </ToggleSwitch>
        </ToggleContainer>
        {minimumRentalDaysEnabled && (
          <InputWrapper>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={minimumRentalDays === null ? '' : minimumRentalDays.toString()}
              onChange={e => {
                const value = e.target.value.trim();
                // Allow empty string for user to clear and re-enter
                if (value === '') {
                  setMinimumRentalDays(null);
                } else {
                  // Only allow numeric input
                  const numValue = parseInt(value);
                  if (!isNaN(numValue) && numValue >= 1) {
                    setMinimumRentalDays(numValue);
                  }
                }
              }}
              onFocus={e => {
                // Move cursor to end of input when focused
                const target = e.target as HTMLInputElement;
                const length = target.value.length;
                target.setSelectionRange(length, length);
              }}
              onKeyDown={e => {
                // Allow backspace, delete, arrow keys, etc.
                if (
                  !/[0-9]/.test(e.key) &&
                  !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(
                    e.key,
                  ) &&
                  !(e.ctrlKey || e.metaKey) // Allow Ctrl+C, Ctrl+V, etc.
                ) {
                  e.preventDefault();
                }
              }}
              placeholder="Enter minimum days"
              required
            />
          </InputWrapper>
        )}
        {rentalDaysError && <ErrorText>{rentalDaysError}</ErrorText>}
      </Section>

      <Section>
        <ToggleContainer>
          <ToggleLabel>Maximum rental days</ToggleLabel>
          <ToggleSwitch>
            <ToggleInput
              type="checkbox"
              checked={maximumRentalDaysEnabled}
              onChange={e => {
                const enabled = e.target.checked;
                setMaximumRentalDaysEnabled(enabled);
                if (!enabled) {
                  setMaximumRentalDays(null);
                } else if (maximumRentalDays === null) {
                  // Set a default value when enabling
                  setMaximumRentalDays(30);
                }
              }}
            />
            <ToggleSlider />
          </ToggleSwitch>
        </ToggleContainer>
        {maximumRentalDaysEnabled && (
          <InputWrapper>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={maximumRentalDays === null ? '' : maximumRentalDays.toString()}
              onChange={e => {
                const value = e.target.value.trim();
                // Allow empty string for user to clear and re-enter
                if (value === '') {
                  setMaximumRentalDays(null);
                } else {
                  // Only allow numeric input
                  const numValue = parseInt(value);
                  if (!isNaN(numValue) && numValue >= 1) {
                    setMaximumRentalDays(numValue);
                  }
                }
              }}
              onFocus={e => {
                // Move cursor to end of input when focused
                const target = e.target as HTMLInputElement;
                const length = target.value.length;
                target.setSelectionRange(length, length);
              }}
              onKeyDown={e => {
                // Allow backspace, delete, arrow keys, etc.
                if (
                  !/[0-9]/.test(e.key) &&
                  !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(
                    e.key,
                  ) &&
                  !(e.ctrlKey || e.metaKey) // Allow Ctrl+C, Ctrl+V, etc.
                ) {
                  e.preventDefault();
                }
              }}
              placeholder="Enter maximum days"
              required
            />
          </InputWrapper>
        )}
        {rentalDaysError && <ErrorText>{rentalDaysError}</ErrorText>}
      </Section>

      <SaveButtonContainer>
        <Button
          theme="filled"
          text="Save"
          onClick={onSubmit}
          isDisabled={loading}
          isLoading={loading}
        />
      </SaveButtonContainer>
    </Container>
  );
};

export default TripPreferences;
